import type { StatusBannerPresentation, StatusBannerVariant, StatusBannerView } from '~/features/my-wallet/types/status-banner'
import type { WalletPaymentModel } from '~/store-v2/my-wallet/useWalletPaymentModel'
import { isWalletBlocked } from '~/features/my-wallet/utils/my-wallet'

// Any other block reason, including null or unknown, falls back to the generic blocked variant.
const BLOCKED_VARIANT_BY_REASON: Partial<Record<string, StatusBannerVariant>> = {
  CNPJ_FINANCIAL_HEALTH: 'blocked_financial_health',
  FRAUD: 'blocked_policy',
}

// Critical delinquency always arrives as a block, so only the warning level is handled here.
const DELINQUENCY_WARNING_STATUS = 'WARNING'

export const STATUS_BANNER_PRESENTATION: Record<StatusBannerVariant, StatusBannerPresentation> = {
  enabled: { tone: 'positive', keyPath: 'my_wallet.status_banner.enabled', hasHighlight: false },
  warning: { tone: 'warning', keyPath: 'my_wallet.status_banner.warning', hasHighlight: true },
  blocked: { tone: 'negative', keyPath: 'my_wallet.status_banner.blocked', hasHighlight: true },
  blocked_policy: { tone: 'negative', keyPath: 'my_wallet.status_banner.blocked_policy', hasHighlight: false },
  blocked_financial_health: { tone: 'negative', keyPath: 'my_wallet.status_banner.blocked_financial_health', hasHighlight: false },
}

/** Returns `null` for an unblocked account without `eligibility_context`, since delinquency cannot be read. */
export function resolveStatusBannerVariant(paymentModel: WalletPaymentModel): StatusBannerVariant | null {
  const { summary, eligibility_context: eligibilityContext } = paymentModel
  if (isWalletBlocked(summary))
    return BLOCKED_VARIANT_BY_REASON[summary.block_reason ?? ''] ?? 'blocked'
  if (!eligibilityContext)
    return null
  if (eligibilityContext.delinquency_status === DELINQUENCY_WARNING_STATUS)
    return 'warning'
  return 'enabled'
}

function isStartingPartner(paymentModel: WalletPaymentModel) {
  const noviceCompleted = paymentModel.summary.novice_completed ?? paymentModel.eligibility_context?.novice_completed
  return paymentModel.summary.state === 'NOVICE' && noviceCompleted === 0
}

export function buildStatusBannerView(paymentModel: WalletPaymentModel): StatusBannerView | null {
  const variant = resolveStatusBannerVariant(paymentModel)
  return variant ? { variant, isStarting: isStartingPartner(paymentModel) } : null
}
